import { DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Action } from '../../../models/core/action';
import { ExportService } from '../../../services/export/export.service';
import { ClaimExportMapper } from '../../../utilities/claim-export-mapper';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ExportFormat } from '../../core/buttons/export-button.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { CheckboxComponent } from '../../core/controls/checkbox.component';
import { InputComponent } from '../../core/controls/input.component';
import { SelectComponent } from '../../core/controls/select.component';
import { TextareaComponent } from '../../core/controls/textarea.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { DialogComponent } from '../../core/panels/dialog.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../core/panels/loading-panel.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { HomeCardComponent } from '../../home/home-card.component';

@Component({
  selector: 'app-diary-types-page',
  imports: [
    LoadingPanelComponent,
    EmptyStateComponent,
    ReactiveFormsModule,
    InputComponent,
    SelectComponent,
    ButtonComponent,
    ActionRowComponent,
    TableComponent,
    ColumnComponent,
    DialogComponent,
    CheckboxComponent,
    TextareaComponent,
    RouterModule,
    MenuComponent,
    ActionButtonComponent,
    PageHeaderComponent,
    HomeCardComponent,
  ],
  template: `
    <app-page-header
      label="Diary types"
      [backRouterLink]="backRouterLink"
      [backLabel]="backLabel"
    />
    <div class="tw-grow tw-w-full tw-justify-center">
      <app-home-card type="Large">
        <div class="tw-pb-8">
          <app-loading-panel
            [state]="state"
            loadingStyle="Skeleton"
            [loadingHeight]="450"
          >
            <ng-template #empty>
              <app-empty-state
                icon="fa-history"
                label="Empty history"
                height="450px"
              >
                <span>TODO empty state</span>
              </app-empty-state>
            </ng-template>

            <ng-template #loaded>
              <form [formGroup]="fg">
                <div class="tw-flex">
                  <app-select
                    [options]="clientOptions"
                    optionLabel="description"
                    optionValue="value"
                    class="tw-basis-1/3"
                    [control]="fg.controls.client"
                    label="Client"
                  />
                </div>
                <app-action-row useMobileLayout>
                  <app-button
                    left1
                    type="submit"
                    (click)="addClick(typeDialog)"
                    [loading]="loading"
                    label="Add"
                    [disabled]="!isClientSelected"
                  />
                </app-action-row>
                <app-table
                  [dataSource]="dataSource"
                  [pageSize]="10"
                  [showSelection]="false"
                  [showExport]="true"
                  [showConfigure]="false"
                  (configure)="(true)"
                  [recordLabel]="'Diary type'"
                  (export)="onExport($event)"
                >
                  <app-column name="diaryType" label="Diary type" />
                  <app-column name="displayed" label="Displayed"> </app-column>
                  <app-column name="order" label="Order" format="n" />
                  <app-column name="defaultText" label="Default text" />
                  <app-column name="createdBy" label="Created by" />
                  <app-column name="createdOn" label="Created On" format="d" />
                  <app-column name="lastModifiedBy" label="Last Modified By" />
                  <app-column
                    name="lastModifiedOn"
                    label="Last Modified On"
                    format="d"
                  />
                  <app-column name="actions" label="Actions" [sort]="false">
                    <ng-template let-data>
                      <app-action-button
                        [menus]="[menu, defaultTypeMenu]"
                        [row]="data"
                        (click)="toggleActionMenu($event, data)"
                      />
                    </ng-template>
                  </app-column>
                </app-table>
                <app-menu
                  [items]="actions"
                  appendTo="body"
                  #menu
                  (click)="onMenu($event, menu.data, typeDialog)"
                />
                <app-menu
                  [items]="defaultTypeActions"
                  appendTo="body"
                  #defaultTypeMenu
                  (click)="onMenu($event, defaultTypeMenu.data, typeDialog)"
                />
              </form>
            </ng-template>
          </app-loading-panel>
        </div>
      </app-home-card>
    </div>

    <app-dialog #typeDialog label="Diary type" width="600px">
      <ng-template #content>
        <div class="tw-py-4">
          <div class="tw-flex">
            <app-input
              class="tw-w-full"
              [control]="fg.controls.diaryType"
              label="Diary type"
            />
          </div>
          <div
            class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-0 sm:tw-items-center"
          >
            <app-input
              class="tw-w-full sm:tw-w-auto tw-min-w-0"
              [control]="fg.controls.order"
              label="Sequence in list"
            />
            <app-checkbox
              label="Display in list"
              [control]="fg.controls.display"
            />
          </div>
          <app-textarea
            [control]="fg.controls.defaultText"
            label="Default text"
          ></app-textarea>
        </div>
        <app-action-row useMobileLayout>
          <app-button
            right1
            (click)="AddDiaryTypeClick(typeDialog)"
            label="Save"
          />
          <app-button
            right1
            (click)="typeDialog.visible = false"
            emphasis="Low"
            class="md:tw-pl-4"
            label="Cancel"
          />
        </app-action-row>
      </ng-template>
    </app-dialog>
  `,
  styles: ``,
})
export class DiaryTypesPageComponent extends BaseComponent implements OnInit {
  private exportService = inject(ExportService);
  private datePipe = inject(DatePipe);

  @Output()
  search = new EventEmitter();

  @Output()
  clear = new EventEmitter();

  @Input()
  loading = false;

  @Input()
  isPageTodo = true;

  @ViewChild('menu') menu!: MenuComponent;
  @ViewChild('defaultTypeMenu') defaultTypeMenu!: MenuComponent;

  backRouterLink = '/todo';

  backLabel = 'My to-do';

  clientOptions = [{ description: '---', value: 'x' }];

  currentTypeId: number;

  isClientSelected = false;
  isClaimSpecific = false;

  protected state: LoadingState = 'Loading';

  fg = new FormGroup({
    client: new FormControl<any>(null, Validators.required),
    diaryType: new FormControl<string>(null, Validators.required),
    order: new FormControl<number>(10),
    display: new FormControl<boolean>(true),
    defaultText: new FormControl<string>(null, [
      Validators.required,
      Validators.maxLength(10000),
    ]),
  });

  actions: Action[] = [
    { label: 'Delete', key: 'delete' },
    { label: 'Edit', key: 'edit' },
  ];

  defaultTypeActions: Action[] = [{ label: 'Edit', key: 'edit' }];

  dataSource: any[] = [];

  async ngOnInit() {
    const data = this.storage.get('current-type');
    this.backRouterLink = data?.backRouterLink || '/todo';
    this.backLabel = data?.backLabel || 'My to-do';

    this.isPageTodo = this.backRouterLink === '/todo';

    const list = await this.api.list.get();
    this.clientOptions = list.clients;

    if (!this.isPageTodo) {
      this.fg.controls.client.disable();
      await this.loadDiaryTypesForCurrentClient();
    }

    this.dispose.subs = this.fg.controls.client.valueChanges.subscribe(
      (value: any) => {
        if (this.fg.controls.client.enabled && value) {
          this.onSelectClient(Number(value));
        }

        if (this.isClientSelected && !value) {
          this.isClientSelected = false;
          this.dataSource = [];
        }
      },
    );

    if (this.clientOptions.length === 1) {
      this.fg.controls.client.setValue(this.clientOptions[0].value);
    }

    this.state = 'Loaded';
  }

  mapSource(diaryTypes: any[]) {
    const source = diaryTypes.map((x: any) => ({
      id: x.typeUID,
      diaryType: x.description,
      displayed: x.isIncluded ? 'Yes' : 'No',
      order: x.order,
      defaultText: x.diaryTypesText,
      createdBy: x.createdBy,
      createdOn: x.dateCreated,
      lastModifiedBy: x.updatedBy,
      lastModifiedOn: x.dateUpdated,
      isDefault: x.isDefault,
    }));
    this.dataSource = source;
  }

  async loadDiaryTypesForCurrentClient() {
    let clientNumber;
    const data = this.storage.get('current-type');
    clientNumber = data?.clientNumber;

    if (!clientNumber) {
      const claim = this.view.currentClaim();
      clientNumber = claim?.contractNumber;
    }

    if (clientNumber) {
      this.fg.controls.client.setValue(String(clientNumber));
      await this.onSelectClient(Number(clientNumber));
    }
  }

  async onSelectClient(clientNumber: number) {
    this.state = 'Loading';

    const diaryTypes = await this.api.clientDiary.getDiaryTypes(clientNumber);
    this.mapSource(diaryTypes);
    this.isClientSelected = true;
    this.state = 'Loaded';
  }

  async onMenu(event: any, data: any, dialog: DialogComponent) {
    if (event.key === 'delete') {
      const result = await this.api.clientDiary.deleteDiaryType([data.id]);
      this.mapSource(result);
    } else {
      this.currentTypeId = data.id;
      this.fg.controls.diaryType.setValue(data.diaryType);
      this.fg.controls.order.setValue(data.order);
      this.fg.controls.display.setValue(
        data.displayed === 'Yes' ? true : false,
      );
      this.fg.controls.defaultText.setValue(data.defaultText);
      if (data.isDefault) {
        this.fg.controls.diaryType.disable();
      } else {
        this.fg.controls.diaryType.enable();
      }
      dialog.visible = true;
    }
  }

  protected addClick(dialog: DialogComponent) {
    this.fg.controls.diaryType.reset();
    this.fg.controls.order.setValue(this.dataSource.length + 1);
    this.fg.controls.display.setValue(true);
    this.fg.controls.defaultText.reset();

    this.currentTypeId = null;
    dialog.visible = true;
  }

  toggleActionMenu(event: any, data: any) {
    if (data.isDefault) {
      this.defaultTypeMenu.toggle(event, data);
    } else {
      this.menu.toggle(event, data);
    }
  }

  protected async AddDiaryTypeClick(dialog: DialogComponent) {
    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      return;
    }

    this.state = 'Loading';

    const data = this.storage.get('current-type');
    const clientNumber =
      data?.isClaimSpecific && data?.clientNumber
        ? Number(data.clientNumber)
        : Number(this.fg.value.client);

    let result: any[];
    if (!this.currentTypeId) {
      result = await this.api.clientDiary.createDiaryType({
        clientNumber: clientNumber,
        diaryType: this.fg.value.diaryType,
        defaultText: this.fg.value.defaultText,
        order: this.fg.value.order,
        display: this.fg.value.display,
      });
    } else {
      result = await this.api.clientDiary.updateDiaryType({
        typeID: this.currentTypeId,
        diaryType: this.fg.getRawValue().diaryType,
        defaultText: this.fg.value.defaultText,
        order: this.fg.value.order,
        display: this.fg.value.display,
      });
    }
    this.mapSource(result);
    dialog.visible = false;
    this.state = 'Loaded';
  }

  async onExport(format: ExportFormat) {
    try {
      let columns = [
        { propertyName: 'diaryType', headerText: 'Diary type' },
        { propertyName: 'displayed', headerText: 'Displayed' },
        { propertyName: 'order', headerText: 'Order' },
        { propertyName: 'defaultText', headerText: 'Default text' },
        { propertyName: 'createdBy', headerText: 'Created by' },
        { propertyName: 'createdOn', headerText: 'Created On' },
        { propertyName: 'lastModifiedBy', headerText: 'Last Modified By' },
        { propertyName: 'lastModifiedOn', headerText: 'Last Modified On' },
      ];
      const excludedProperties = new Set();
      if (!(this as any).showTitle) excludedProperties.add('title');
      columns = columns.filter(
        (col) => !excludedProperties.has(col.propertyName),
      );
      const rows = this.dataSource;
      const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
      const filename = `Diary_${formattedDate}.${format}`;
      if (format === 'csv') {
        const csvData = ClaimExportMapper.toCsvExport(rows, columns, 'Diary');
        await this.exportService.exportCsv(csvData, filename);
      } else if (format === 'xlsx') {
        const excelData = ClaimExportMapper.toExcelExport(
          rows,
          columns,
          'Diary_types',
          'OmniInsights Export',
        );
        await this.exportService.exportExcel(excelData, filename);
      }
    } catch (err) {
      if ((this as any).handleError) {
        (this as any).handleError(err);
      } else {
        (this as any).error = err;
      }
    }
  }
}
